// @flow

import { useCallback } from 'react';

import { useDispatch } from 'react-redux';

import { NOTIFICATION_ID as CART_NOTIFICATION_ID } from '@gandi/cart';
import {
  notifyDanger,
  notifyWarning,
  closeNotification,
} from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';

import { RegisterButton } from 'src/Domain/Fallback/RegisterButton';
import { ProvidedRowContainer } from 'src/Domain/ProvidedRowContainer';

import { addToCart, removeFromCart } from '../modules/actions';
import type { ExtendedResult } from '../types';

import { RenewPriceWithOption } from './RenewPriceWithOption';

import {
  getDomainPossibility,
  renewPossibility2AvailabilityIconName,
  useAvailabilityText,
} from './modules';

type Props = { domain: ExtendedResult };

export function DomainRow({ domain }: Props): React.MixedElement {
  const possibility = getDomainPossibility(domain);
  const availabilityIconName =
    renewPossibility2AvailabilityIconName[possibility];
  const availabilityText = useAvailabilityText({ domain });

  const translator = useTranslator();
  const { __ } = translator;

  const dispatch = useDispatch();

  const handleError: (error: Error & any) => void = useCallback(
    error => {
      // $FlowFixMe[incompatible-call]
      dispatch(closeNotification(CART_NOTIFICATION_ID));

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        if (error.response.status === 403) {
          dispatch(
            notifyWarning({
              message: error.response.data.message,
              title: __('Insufficient permissions'),
            }),
          );
        } else {
          dispatch(
            notifyDanger({
              message: error.response.data.message,
            }),
          );
        }
      } else {
        dispatch(notifyDanger(__('Unexpected error occurred')));
      }
    },
    [__, dispatch],
  );

  const handleClickItem = useCallback(async () => {
    try {
      if (domain.addedToCart) {
        await dispatch(removeFromCart(domain));
      } else {
        await dispatch(addToCart(domain, translator));
      }
    } catch (error) {
      handleError(error);
    }
  }, [dispatch, domain, handleError, translator]);

  const loading =
    possibility === 'loading' ||
    domain.loading ||
    ['adding', 'removing'].includes(domain.cartProductState);

  return (
    <ProvidedRowContainer
      id={domain.fqdn}
      addedToCart={domain.addedToCart}
      availabilityIconName={availabilityIconName}
      fqdn={domain.fqdn}
      onClickItem={handleClickItem}
      availabilityText={availabilityText}
      loading={loading}
      isCartActionnable={
        ['renewable', 'restorable'].includes(possibility) ||
        (possibility === 'creatable' && domain.addedToCart)
      }
    >
      {['renewable', 'restorable'].includes(possibility) &&
        domain.quotation && (
          <RenewPriceWithOption quotedProduct={domain.quotation} />
        )}
      {possibility === 'creatable' && !domain.addedToCart && (
        <RegisterButton fqdn={domain.fqdn} />
      )}
    </ProvidedRowContainer>
  );
}
